/* The colour chip — the one bold thing in an otherwise achromatic interface.
 *
 * Someone scanning the board wants two facts before anything else: what colour,
 * and how shiny. The swatch answers the first, the sheen dots the second. The
 * hex is approximate and for reading the board only; the brand code beneath it
 * is what actually gets re-ordered. */
import { colourOf, useApp } from '@/store/useApp';

interface SwatchProps {
  /** A colour code off the shop's list. */
  code: string;
  /** Edge length in px. The corner radius scales with it. */
  size: number;
  className?: string;
}

export function Swatch({ code, size, className }: SwatchProps) {
  const colour = useApp((s) => colourOf(s, code));
  return (
    <span
      className={className}
      title={colour ? `${colour.brand} ${colour.name} (${colour.code})` : code}
      style={{
        display: 'block',
        flex: 'none',
        width: size,
        height: size,
        background: colour?.hex ?? 'transparent',
        border: '1px solid var(--chip-border)',
        borderRadius: Math.max(5, Math.round(size / 4)),
        // A pale inner edge so a near-white chip still reads as an object on a
        // white surface.
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.5)',
      }}
    />
  );
}

interface SheenBarsProps {
  /** Index into SHEENS: matt through gloss. */
  level: number;
  label?: string;
  /** Diameter of one dot. Four of them plus their gaps is what the chip column
   *  is wide, so this is the number that decides whether a job code can sit
   *  beside the colour rather than wrap under it. */
  dot?: number;
}

/** Sheen indicator: fill runs matt through gloss. */
export function SheenBars({ level, label, dot = 8 }: SheenBarsProps) {
  return (
    <span
      style={{ display: 'flex', gap: Math.max(2, Math.round(dot * 0.375)) }}
      title={label ?? 'Sheen'}
    >
      {[0, 1, 2, 3].map((i) => (
        <i
          key={i}
          style={{
            display: 'block',
            width: dot,
            height: dot,
            borderRadius: '50%',
            border: '1px solid var(--dim)',
            background: i <= level ? 'var(--ink)' : 'transparent',
          }}
        />
      ))}
    </span>
  );
}

/** Initials in a ring — who is on a job. */
export function Avatar({ initials, size = 32 }: { initials: string; size?: number }) {
  return (
    <span
      style={{
        width: size,
        height: size,
        flex: 'none',
        borderRadius: '50%',
        border: '1px solid var(--line)',
        background: 'var(--ground)',
        fontFamily: 'var(--font-data)',
        fontSize: Math.max(10, Math.round(size * 0.34)),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--ink)',
      }}
    >
      {initials}
    </span>
  );
}
